import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { Attendee } from "../../redux/types/types";
import { getAttendeeSpecialClassAccessApi } from "../../utils/api/attendees";
import { getLocalizedText } from "../../utils/localization";

interface SpecialClassAccessDisplayProps {
  attendee: Attendee;
}

interface ClassAccessInfo {
  classId: number;
  classTitle: Record<string, string>; // JSON object with localized titles
  isSpecial: boolean;
  remainingAccess: number;
}

export default function SpecialClassAccessDisplay({
  attendee,
}: SpecialClassAccessDisplayProps) {
  const intl = useIntl();
  const [classAccessInfo, setClassAccessInfo] = useState<ClassAccessInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchClassAccessInfo = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getAttendeeSpecialClassAccessApi(attendee.id);
        setClassAccessInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch class access info:", error);
        setError(
          intl.formatMessage({ id: "attendee.specialAccess.fetchError" })
        );
      } finally {
        setLoading(false);
      }
    };

    fetchClassAccessInfo();
  }, [attendee.id, attendee.specialClassAccess, intl]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={1}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }

  if (classAccessInfo.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
          borderRadius: 1,
          p: 0.5,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          <FormattedMessage
            id="attendee.specialAccess.title"
            defaultMessage="Special Class Access ({count})"
            values={{ count: classAccessInfo.length }}
          />
        </Typography>
        <IconButton
          size="small"
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {classAccessInfo.map((info) => (
            <Chip
              key={info.classId}
              label={`${getLocalizedText(info.classTitle, intl.locale)}: ${
                info.remainingAccess
              } ${intl.formatMessage({
                id: "attendee.accessCount",
                defaultMessage: "classes remaining",
              })}`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: "0.75rem" }}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
